import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { FocusEvent, PointerEvent, ReactElement, RefObject } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Button, IconButton, Slide, Stack, Typography, useMediaQuery } from '@mui/material'
import type { NotificationToast } from '../../hooks/useNotificationToasts'
import { getNotificationKind } from './notificationKinds'
import { NOTIFICATIONS_TEXT } from './notificationsText'

export const NOTIFICATION_TOAST_AUTO_CLOSE_MS = 6000

export interface NotificationToastStackProps {
  toasts: NotificationToast[]
  onOpen: (notificationId: number) => void
  onMarkRead: (notificationId: number) => void
  onClose: (notificationId: number) => void
  /** Gets the focus when a focused toast is closed with one of its buttons (the bell in the header). */
  returnFocusRef?: RefObject<HTMLElement | null>
}

const TOAST_BACKGROUND = '#15202b'
const LINK_COLOR = '#9fc0ff'

const linkButtonSx = {
  minWidth: 0,
  minHeight: 0,
  p: 0,
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.45,
  textTransform: 'none',
  '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
  '&.Mui-disabled': { color: 'inherit', opacity: 0.5 }
} as const

const visuallyHiddenSx = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0
} as const

interface NotificationToastCardProps {
  toast: NotificationToast
  onOpen: (notificationId: number) => void
  onMarkRead: (notificationId: number) => void
  onClose: (notificationId: number) => void
  onReturnFocus: () => void
}

/**
 * One toast: closes itself after six seconds, the timer stops while the mouse pointer or the focus is on it or an
 * action runs. A touch tap does not stop the timer (there is no pointer leave after it).
 */
function NotificationToastCard({ toast, onOpen, onMarkRead, onClose, onReturnFocus }: NotificationToastCardProps): ReactElement {
  const [isHovered, setIsHovered] = useState(false)
  const [hasFocus, setHasFocus] = useState(false)
  const remainingMsRef = useRef(NOTIFICATION_TOAST_AUTO_CLOSE_MS)
  const cardRef = useRef<HTMLDivElement>(null)
  /** A button of the focused toast was pressed: when the toast goes away, the focus goes to the bell. */
  const returnFocusOnCloseRef = useRef(false)
  const onCloseRef = useRef(onClose)
  const onReturnFocusRef = useRef(onReturnFocus)
  const { notificationId, pending } = toast
  const isPaused = isHovered || hasFocus || pending
  const kind = getNotificationKind(toast.eventType)
  const Icon = kind.Icon

  useEffect(() => {
    onCloseRef.current = onClose
    onReturnFocusRef.current = onReturnFocus
  }, [onClose, onReturnFocus])

  useEffect(() => {
    if (isPaused) {
      return
    }
    const startedAt = Date.now()
    const timer = setTimeout(() => onCloseRef.current(notificationId), remainingMsRef.current)
    return () => {
      clearTimeout(timer)
      remainingMsRef.current = Math.max(0, remainingMsRef.current - (Date.now() - startedAt))
    }
  }, [isPaused, notificationId])

  // Layout cleanup: the card is still in the document and may still hold the focus.
  useLayoutEffect(() => {
    const card = cardRef.current
    return () => {
      if (!returnFocusOnCloseRef.current || !card) {
        return
      }
      const active = document.activeElement
      if (active === null || active === document.body || card.contains(active)) {
        onReturnFocusRef.current()
      }
    }
  }, [])

  function handleBlur(event: FocusEvent<HTMLElement>): void {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setHasFocus(false)
    }
  }

  function handlePointerHover(event: PointerEvent<HTMLElement>, hovered: boolean): void {
    if (event.pointerType === 'mouse') {
      setIsHovered(hovered)
    }
  }

  function runWithFocusReturn(action: (notificationId: number) => void): void {
    returnFocusOnCloseRef.current = cardRef.current?.contains(document.activeElement) ?? false
    action(notificationId)
  }

  return (
    <Box
      ref={cardRef}
      role="group"
      aria-label={toast.title}
      onPointerEnter={(event) => handlePointerHover(event, true)}
      onPointerLeave={(event) => handlePointerHover(event, false)}
      onFocus={() => setHasFocus(true)}
      onBlur={handleBlur}
      sx={{
        pointerEvents: 'auto',
        display: 'grid',
        gridTemplateColumns: '32px minmax(0, 1fr) auto',
        gap: '12px',
        alignItems: 'start',
        width: 360,
        maxWidth: 'calc(100vw - 32px)',
        boxSizing: 'border-box',
        p: '12px 14px',
        borderRadius: '10px',
        bgcolor: TOAST_BACKGROUND,
        color: '#fff',
        boxShadow: '0 10px 28px rgba(21,32,43,.16)'
      }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,.14)',
          color: '#fff',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="p"
          onClick={pending ? undefined : () => onOpen(notificationId)}
          sx={{
            m: 0,
            fontSize: 13.5,
            lineHeight: 1.4,
            color: '#fff',
            overflowWrap: 'anywhere',
            cursor: pending ? 'default' : 'pointer'
          }}
        >
          {toast.title}
        </Typography>
        <Stack direction="row" spacing="14px" sx={{ mt: '4px' }}>
          <Button
            variant="text"
            disableRipple
            disabled={pending}
            onClick={() => runWithFocusReturn(onOpen)}
            sx={{ ...linkButtonSx, color: LINK_COLOR }}
          >
            {NOTIFICATIONS_TEXT.toastOpen}
          </Button>
          <Button
            variant="text"
            disableRipple
            disabled={pending}
            onClick={() => runWithFocusReturn(onMarkRead)}
            sx={{ ...linkButtonSx, color: 'rgba(255,255,255,.7)' }}
          >
            {NOTIFICATIONS_TEXT.toastMarkRead}
          </Button>
        </Stack>
      </Box>
      <IconButton
        aria-label={NOTIFICATIONS_TEXT.toastClose}
        onClick={() => runWithFocusReturn(onClose)}
        sx={{ width: 28, height: 28, borderRadius: '8px', color: 'rgba(255,255,255,.8)', '&:hover': { bgcolor: 'rgba(255,255,255,.1)' } }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  )
}

interface ToastAnnouncement {
  notificationId: number
  title: string
}

/**
 * The title of the newest toast that has just appeared, for the live region. A closed toast is not announced again,
 * and the text goes away when its toast closes, so the region never re-reads older titles.
 */
function useLatestToastAnnouncement(toasts: NotificationToast[]): ToastAnnouncement | null {
  const [announcement, setAnnouncement] = useState<ToastAnnouncement | null>(null)
  const [knownIds, setKnownIds] = useState<readonly number[]>([])
  const ids = toasts.map((toast) => toast.notificationId)
  if (ids.join(',') !== knownIds.join(',')) {
    setKnownIds(ids)
    const added = toasts.find((toast) => !knownIds.includes(toast.notificationId))
    if (added) {
      setAnnouncement({ notificationId: added.notificationId, title: added.title })
    } else if (announcement && !ids.includes(announcement.notificationId)) {
      setAnnouncement(null)
    }
  }
  return announcement
}

/**
 * New-notification toasts under the app bar on the right, newest on top. The stack sits below the drawers, so an
 * open notifications panel or navigation drawer covers it. Screen readers hear only the title of the newest toast,
 * from a separate polite live region; the buttons stay in the visible stack.
 */
export default function NotificationToastStack({
  toasts,
  onOpen,
  onMarkRead,
  onClose,
  returnFocusRef
}: NotificationToastStackProps): ReactElement {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true })
  const returnFocus = useCallback(() => {
    returnFocusRef?.current?.focus()
  }, [returnFocusRef])
  const announcement = useLatestToastAnnouncement(toasts)

  return (
    <>
      <Box role="status" aria-live="polite" sx={visuallyHiddenSx}>
        {announcement && <span key={announcement.notificationId}>{announcement.title}</span>}
      </Box>
      {toasts.length > 0 && (
        <Stack
          component="section"
          aria-label={NOTIFICATIONS_TEXT.toastsRegion}
          spacing="8px"
          sx={{
            position: 'fixed',
            top: { xs: 56 + 12, sm: 68 + 12 },
            right: 16,
            zIndex: (theme) => theme.zIndex.drawer - 1,
            alignItems: 'flex-end',
            pointerEvents: 'none'
          }}
        >
          {toasts.map((toast) => {
            const card = (
              <NotificationToastCard
                toast={toast}
                onOpen={onOpen}
                onMarkRead={onMarkRead}
                onClose={onClose}
                onReturnFocus={returnFocus}
              />
            )
            return prefersReducedMotion ? (
              <Box key={toast.notificationId}>{card}</Box>
            ) : (
              <Slide key={toast.notificationId} in appear direction="left">
                <Box>{card}</Box>
              </Slide>
            )
          })}
        </Stack>
      )}
    </>
  )
}
